import { forwardRef, useContext, useEffect, useMemo } from 'react';
import { BlendFunction, KernelSize, OutlineEffect } from 'postprocessing';
import { Object3D, Texture } from 'three';

import { useThree } from '@react-three/fiber';
import { Instance, isInstance } from '@spazious/dollhouse-three';

import { useSelection } from '../components/Selection/index.js';
import useInteractiveObjects from '../hooks/useInteractiveObjects.js';

import { EffectComposerContext } from './EffectComposer.js';

export type OutlineProps = {
  selectionLayer?: number;
  blendFunction?: BlendFunction;
  patternTexture?: Texture;
  patternScale?: number;
  edgeStrength?: number;
  pulseSpeed?: number;
  visibleEdgeColor?: number | string;
  hiddenEdgeColor?: number | string;
  multisampling?: number;
  resolutionScale?: number;
  resolutionX?: number;
  resolutionY?: number;
  width?: number;
  height?: number;
  kernelSize?: KernelSize;
  blur?: boolean;
  xRay?: boolean;
};

export const Outline = forwardRef<OutlineEffect, OutlineProps>(
  (
    {
      selectionLayer = 10,
      blendFunction,
      patternTexture,
      edgeStrength,
      pulseSpeed,
      visibleEdgeColor,
      hiddenEdgeColor,
      width,
      height,
      kernelSize,
      blur,
      xRay,
    },
    ref
  ) => {
    const invalidate = useThree((state) => state.invalidate);
    const { scene, camera } = useContext(EffectComposerContext);

    const sceneObjects = useInteractiveObjects();

    const effect = useMemo(
      () =>
        new OutlineEffect(scene, camera, {
          blendFunction,
          patternTexture,
          edgeStrength,
          pulseSpeed,
          visibleEdgeColor: visibleEdgeColor as any,
          hiddenEdgeColor: hiddenEdgeColor as any,
          width,
          height,
          kernelSize,
          blur,
          xRay,
        }),
      [
        blendFunction,
        blur,
        camera,
        edgeStrength,
        height,
        hiddenEdgeColor,
        kernelSize,
        patternTexture,
        pulseSpeed,
        scene,
        visibleEdgeColor,
        width,
        xRay,
      ]
    );

    const api = useSelection();

    useEffect(() => {
      effect.selectionLayer = selectionLayer;
      invalidate();
    }, [effect, invalidate, selectionLayer]);

    useEffect(() => {
      let lastSelectedInstances = new Set<Instance>();

      const clear = () => {
        lastSelectedInstances.forEach((node) => {
          if (isInstance(node)) {
            node.selected = false;
          }
        });
      };

      if (api && api.enabled) {
        if (api.selected?.length) {
          const [objects, instances] = api.selected.reduce(
            (acc, node) => {
              if (isInstance(node)) {
                acc[1].push(node);
              } else {
                let found = false;
                if ((node as any).isGroup) {
                  node.traverse((child) => {
                    if (isInstance(child)) {
                      acc[1].push(child);
                      found = true;
                    }
                  });
                }
                if (!found) {
                  acc[0].push(node);
                }
              }

              return acc;
            },
            [[], []] as [Object3D[], Instance[]]
          );

          effect.selection.set(objects);
          clear();

          instances.forEach((node) => {
            if (isInstance(node) && visibleEdgeColor) {
              node.selected = true;
              lastSelectedInstances.add(node);
            }
          });

          invalidate();
          return () => {
            clear();
            effect.selection.clear();
            invalidate();
          };
        }
      }
    }, [api, effect.selection, invalidate, visibleEdgeColor, sceneObjects]);

    useEffect(() => {
      return () => {
        effect.dispose();
      };
    }, [effect]);

    return <primitive ref={ref} object={effect} />;
  }
);
